import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "./components/api";

const PackageContext = createContext();

export const PackageProvider = ({ children }) => {
    const [packageDetails, setPackageDetails] = useState(undefined); 
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const location = useLocation();

    const authPaths = [
        '/local-admin/add-lead',
        '/customer/login',
        '/customer/register',
        '/customer/reset-link',
        '/customer/reset-password',
        '/customer/logout',
        '/customer/dashboard',
        '/customer/orders',
        '/customer/wishlist',
        '/customer/change-password',
        '/customer/addresses',
        '/customer/addaddress',
        '/customer/update-address',
        '/customer/add-review',
        '/category',
        '/categories',
        '/products',
        '/product',
        '/about',
        '/checkout',
        '/confirm-order',
        '/order/failed',
        '/sellers',
        '/terms',
        '/privacy',
        '/help-center',
        '/loader',
        '/error404',
        '/link-in-bio',
        '/woocommerce-plugin',
        '/sales-customer-capture',
        '/customer-memory',
        '/payments',
        '/repeat-revenue-agent',
        '/maeven',
        '/customer-retarget-assistant',
        '/conversation/verify',
        '/conversation/product-card',
        '/conversation/store-card',
        '/',
    ];

    // 🔥 NEW: Explicit seller pages that should NOT fetch vendor-package
    const sellerAuthOnly = [
        '/seller/login',
        '/seller/register',
        '/seller/reset-link',
        '/s/reset-password',
        '/seller/staff_login',
        '/seller/staff-reset-link',
        '/seller/staff-reset-password',
        '/seller/register-success',
    ];

    // 🔥 Detect seller pages
    const isSellerPage = location.pathname.startsWith("/seller");

    // 🔥 Updated isAuthPage logic
    const isAuthPage =
        sellerAuthOnly.includes(location.pathname) || // exempt seller login/register pages
        (!isSellerPage && ( // normal auth pages
            authPaths.includes(location.pathname) ||
            /^\/category\/[^/]+\/\d+$/.test(location.pathname) ||
            /^\/product\/[^/]+\/\d+$/.test(location.pathname) ||
            /^\/order\/success\/[^/]+$/.test(location.pathname) ||
            /^\/s\/reset-password\/[^/]+\/?$/.test(location.pathname) ||
            /^\/customer\/reset-password\/[^/]+\/?$/.test(location.pathname) ||
            /^\/set-password-lead\/[^/]+\/?$/.test(location.pathname) ||
            /^\/set-password\/[^/]+$/.test(location.pathname) ||
            /^\/newsletter\/[^/]+$/.test(location.pathname) ||
            /^\/receipt\/\d{4}-\d{2}-\d{2}\/[^/]+$/.test(location.pathname) ||
            /^\/[^/]+$/.test(location.pathname) ||
            /^\/[^/]+\/[^/]+\/\d+$/.test(location.pathname)
        ));

    const fetchPackageDetails = async () => {
        try {
            const token = localStorage.getItem("access_token");

            // ❌ Don't fetch on auth pages
            if (isAuthPage) {
                setPackageDetails(undefined);
                return;
            }

            const response = await api.get(baseUrl + "/vendor-package/", {
                headers: { Authorization: `Bearer ${token}` },
            });

            const data = response.data;

            // ✅ Normalize backend response
            if (!data || data.status === "0") {
                setPackageDetails(null);
            } else {
                setPackageDetails(data);
            }

        } catch (error) {
            setPackageDetails(undefined);
        }
    };

    useEffect(() => {
        setPackageDetails(undefined); // reset to loading on route change
        fetchPackageDetails();
    }, [location.pathname]);

    return (
        <PackageContext.Provider value={{ packageDetails, fetchPackageDetails }}>
            {children}
        </PackageContext.Provider>
    );
};

export const usePackage = () => useContext(PackageContext);